// Figma: "Loyalty Cloud" file, node 18428:55540 ("New transaction" dialog), opened from the
// "New transaction" button in ./ResidentDetailDrawer.js. Submits {transactionType, amount};
// ResidentDetailDrawer maps transactionType to its label and sends {amount, description}
// to residentsSlice.js's createResidentTransactions mutation (resident/{id}/transactions/).
import React from 'react';
import PropTypes from 'prop-types';
import {Box, Button, Checkbox, Dialog, MenuItem, Select, Stack, TextField, Typography} from '@mui/material';

import {tokens} from '../../../themes/ThemeCustomization';
import buttonSpinnerIcon from '../../../assets/auth/button-spinner-icon.svg';
import SpinnerIcon from '../../authentication/components/SpinnerIcon';
import checkIcon from '../../../assets/residentDetail/check-icon.svg';

export const TRANSACTION_TYPES = [
    {value: 'bonus', label: 'Bonus'},
    {value: 'rent_match', label: 'Rent Match'},
    {value: 'stake_bonus', label: 'Stake Bonus'}
];

const FieldLabel = ({children}) => (
    <Typography variant="body2" sx={{color: tokens.color.neutral950, pb: 1}}>
        {children}
    </Typography>
);

const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: tokens.radius.control,
        fontSize: '14px',
        '& fieldset': {borderColor: tokens.color.neutral200},
        '&:hover fieldset': {borderColor: tokens.color.neutral300},
        '&.Mui-focused fieldset': {borderColor: tokens.color.sky600, borderWidth: '1px'}
    }
};

const NewTransactionDialog = ({open, resident, onClose, onSubmit, submitting}) => {
    const [transactionType, setTransactionType] = React.useState(TRANSACTION_TYPES[0].value);
    const [amount, setAmount] = React.useState('');
    const [acknowledged, setAcknowledged] = React.useState(false);

    React.useEffect(() => {
        if (!open) return;
        setTransactionType(TRANSACTION_TYPES[0].value);
        setAmount('');
        setAcknowledged(false);
    }, [open]);

    if (!resident) return null;

    const handleSubmit = () => {
        onSubmit?.({transactionType, amount});
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: '540px',
                    maxHeight: '720px',
                    borderRadius: '12px',
                    border: `1px solid ${tokens.color.neutral300}`
                }
            }}
        >
            <Box sx={{p: '20px', overflowY: 'auto'}}>
                <Typography sx={{fontSize: '20px', fontWeight: 600, lineHeight: '28px', color: tokens.color.neutral950, pb: 1}}>
                    New transaction
                </Typography>
                <Typography variant="body2" sx={{color: tokens.color.neutral600, pb: '20px'}}>
                    Send a bonus, rent match, or stake bonus directly to this resident&apos;s account.
                </Typography>

                <Stack spacing={1.5} sx={{opacity: submitting ? 0.6 : 1, pointerEvents: submitting ? 'none' : 'auto'}}>
                    <Box>
                        <FieldLabel>Transaction type</FieldLabel>
                        <Select
                            fullWidth
                            size="small"
                            value={transactionType}
                            onChange={(e) => setTransactionType(e.target.value)}
                            renderValue={(value) => TRANSACTION_TYPES.find((type) => type.value === value)?.label}
                            sx={inputSx}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        mt: 0.5,
                                        borderRadius: '12px',
                                        border: `1px solid ${tokens.color.neutral200}`,
                                        boxShadow: '0px 1px 3px 0px rgba(0,0,0,0.1), 0px 1px 2px 0px rgba(0,0,0,0.06)',
                                        p: 1
                                    }
                                },
                                MenuListProps: {sx: {p: 0}}
                            }}
                        >
                            {TRANSACTION_TYPES.map(({value, label}) => (
                                <MenuItem
                                    key={value}
                                    value={value}
                                    sx={{
                                        height: 36,
                                        px: 1,
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        color: tokens.color.neutral950,
                                        justifyContent: 'space-between',
                                        '&.Mui-selected': {backgroundColor: tokens.color.neutral200},
                                        '&.Mui-selected:hover': {backgroundColor: tokens.color.neutral200},
                                        '&:hover': {backgroundColor: tokens.color.neutral100}
                                    }}
                                >
                                    {label}
                                    {transactionType === value && (
                                        <Box component="img" src={checkIcon} alt="" sx={{width: 16, height: 16}} />
                                    )}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    <Box>
                        <FieldLabel>Amount</FieldLabel>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            placeholder="0.00"
                            InputProps={{startAdornment: <Typography sx={{color: tokens.color.neutral500, pr: 0.5}}>$</Typography>}}
                            sx={inputSx}
                        />
                    </Box>

                    <Stack direction="row" alignItems="flex-start" spacing={1} sx={{pt: 0.5}}>
                        <Checkbox
                            checked={acknowledged}
                            onChange={(e) => setAcknowledged(e.target.checked)}
                            sx={{
                                p: 0,
                                mt: '2px',
                                color: tokens.color.neutral300,
                                '&.Mui-checked': {color: tokens.color.sky600},
                                '& .MuiSvgIcon-root': {fontSize: 20}
                            }}
                        />
                        <Typography variant="body2" sx={{color: tokens.color.neutral600, flex: 1}}>
                            I acknowledge this transaction will be applied to the resident&apos;s account
                        </Typography>
                    </Stack>
                </Stack>
            </Box>

            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                    p: 1.5,
                    backgroundColor: tokens.color.neutral50,
                    borderTop: `1px solid ${tokens.color.neutral200}`
                }}
            >
                <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={submitting}
                    sx={{
                        height: 32,
                        px: 1.5,
                        color: tokens.color.neutral950,
                        borderColor: tokens.color.neutral200,
                        '&.Mui-disabled': {opacity: 0.6, color: tokens.color.neutral950, borderColor: tokens.color.neutral200}
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting || !amount || !acknowledged}
                    sx={{
                        backgroundColor: tokens.color.sky600,
                        height: 32,
                        px: 1.5,
                        '&.Mui-disabled': {backgroundColor: tokens.color.sky600, opacity: 0.6, color: tokens.color.white}
                    }}
                >
                    {submitting ? (
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                            <SpinnerIcon src={buttonSpinnerIcon} size={12} />
                            <Typography variant="body2" fontWeight={500} sx={{color: tokens.color.white}}>
                                Submit
                            </Typography>
                        </Stack>
                    ) : (
                        'Submit'
                    )}
                </Button>
            </Stack>
        </Dialog>
    );
};

NewTransactionDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    resident: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func,
    submitting: PropTypes.bool
};

export default NewTransactionDialog;
